import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Loader2, ChevronDown, ChevronRight, FileText, Stethoscope, Baby, Trash2, Heart, Activity } from "lucide-react";
import { toast } from "sonner";
import { safeFormatDate, safeDistanceToNow } from "@/lib/dates";

const STATUS_LABELS = {
  in_foal: "In foal",
  foaled: "Foaled",
  lost: "Lost",
  weaned: "Weaned",
};
const STATUS_TONE = {
  in_foal: "bg-[hsl(var(--ap-info)/0.18)] text-[hsl(var(--ap-info))]",
  foaled: "bg-[hsl(var(--ap-success)/0.18)] text-[hsl(var(--ap-success))]",
  lost: "bg-destructive/15 text-destructive",
  weaned: "bg-muted text-muted-foreground",
};

export default function BreedingPage() {
  const { hasPerm } = useAuth();
  const canWrite = hasPerm("breeding:write");

  const [records, setRecords] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  // Breeding stock — every female horse (sex ∈ mare/filly) in the herd.
  // Fed to the mare picker so a breeding record always FKs to a real
  // horse instead of a stringly-typed name.
  const [breedingStock, setBreedingStock] = useState([]);
  // Foal history dialog (opened from the "N total" pill on any row).
  const [foalDialogFor, setFoalDialogFor] = useState(null); // {record, mare_name}
  const [foalHistory, setFoalHistory] = useState([]);
  const [foalHistoryLoading, setFoalHistoryLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    mare_id: "", mare_name: "", sire_name: "", dam_name: "", sire_of_dam: "",
    conceived_date: "", due_date: "", status: "in_foal", notes: "",
  });
  const [adding, setAdding] = useState(false);

  const [logOpen, setLogOpen] = useState(false);
  const [logForm, setLogForm] = useState({ date: "", title: "", body: "" });
  const [postingLog, setPostingLog] = useState(false);

  const [vetNoteFor, setVetNoteFor] = useState(null); // record being annotated
  const [vetNoteForm, setVetNoteForm] = useState({ date: "", note: "", vet_name: "" });
  const [savingVetNote, setSavingVetNote] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, l, stock] = await Promise.all([
        api.get("/breeding/records"),
        api.get("/breeding/logs?limit=50"),
        api.get("/horses/breeding-stock/list"),
      ]);
      setRecords(r.data);
      setLogs(l.data);
      setBreedingStock(stock.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load breeding data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openFoalHistory = async (record) => {
    if (!record?.mare_id) {
      toast.info("Foal history only available once the mare is linked to a horse profile.");
      return;
    }
    setFoalDialogFor({ record, mare_name: record.mare_name });
    setFoalHistoryLoading(true);
    try {
      const r = await api.get(`/breeding/mares/${record.mare_id}/foals`);
      setFoalHistory(r.data);
    } catch (e) {
      toast.error("Could not load foal history");
      setFoalHistory([]);
    } finally { setFoalHistoryLoading(false); }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const createRecord = async () => {
    if (!addForm.mare_id) { toast.error("Please pick a mare from the herd"); return; }
    setAdding(true);
    try {
      const res = await api.post("/breeding/records", addForm);
      setRecords((prev) => [res.data, ...prev]);
      toast.success(`Added ${addForm.mare_name}`);
      setAddOpen(false);
      setAddForm({ mare_id: "", mare_name: "", sire_name: "", dam_name: "", sire_of_dam: "",
                   conceived_date: "", due_date: "", status: "in_foal", notes: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add mare");
    } finally { setAdding(false); }
  };

  const deleteRecord = async (rec) => {
    try {
      await api.delete(`/breeding/records/${rec.id}`);
      setRecords((prev) => prev.filter((r) => r.id !== rec.id));
      toast.success(`Removed ${rec.mare_name}`);
    } catch {
      toast.error("Failed to delete");
    }
  };

  const postLog = async () => {
    if (!logForm.title) { toast.error("Title required"); return; }
    setPostingLog(true);
    try {
      const res = await api.post("/breeding/logs", logForm);
      setLogs((prev) => [res.data, ...prev]);
      toast.success("Activity logged");
      setLogOpen(false);
      setLogForm({ date: "", title: "", body: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to log");
    } finally { setPostingLog(false); }
  };

  const addVetNote = async () => {
    if (!vetNoteForm.note) { toast.error("Note text required"); return; }
    setSavingVetNote(true);
    try {
      const res = await api.post(`/breeding/records/${vetNoteFor.id}/vet-notes`, vetNoteForm);
      // Optimistically append to the record's vet_notes array
      setRecords((prev) => prev.map((r) =>
        r.id === vetNoteFor.id ? { ...r, vet_notes: [...(r.vet_notes || []), res.data] } : r
      ));
      toast.success("Vet note added");
      setVetNoteFor(null);
      setVetNoteForm({ date: "", note: "", vet_name: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSavingVetNote(false); }
  };

  const inFoal = records.filter((r) => r.status === "in_foal");
  const others = records.filter((r) => r.status !== "in_foal");

  return (
    <div className="space-y-6">
      <PageHeader
        title="Breeding"
        subtitle={`${inFoal.length} mare${inFoal.length === 1 ? "" : "s"} currently in foal · ${records.length} total records`}
        actions={
          canWrite && (
            <div className="flex items-center gap-2">
              <Dialog open={logOpen} onOpenChange={setLogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="breeding-add-log-button">
                    <FileText className="h-4 w-4 mr-2" /> Log activity
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="breeding-add-log-dialog">
                  <DialogHeader>
                    <DialogTitle>Log breeding activity</DialogTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      Free-text note dated to today (or the date you provide).
                    </p>
                  </DialogHeader>
                  <div className="space-y-3 pt-2">
                    <div>
                      <Label>Date (optional)</Label>
                      <Input type="date" value={logForm.date}
                             onChange={(e) => setLogForm({ ...logForm, date: e.target.value })}
                             data-testid="breeding-log-date" />
                    </div>
                    <div>
                      <Label>Title</Label>
                      <Input value={logForm.title}
                             onChange={(e) => setLogForm({ ...logForm, title: e.target.value })}
                             placeholder="e.g. Stallion booking confirmed for next season"
                             data-testid="breeding-log-title" />
                    </div>
                    <div>
                      <Label>Details</Label>
                      <Textarea rows={5} value={logForm.body}
                                onChange={(e) => setLogForm({ ...logForm, body: e.target.value })}
                                data-testid="breeding-log-body" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setLogOpen(false)}>Cancel</Button>
                    <Button onClick={postLog} disabled={postingLog} data-testid="breeding-log-submit">
                      {postingLog ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Post
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="breeding-add-record-button">
                    <Plus className="h-4 w-4 mr-2" /> Add mare in foal
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl" data-testid="breeding-add-record-dialog">
                  <DialogHeader><DialogTitle>Add mare in foal</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div className="col-span-2">
                      <Label>Mare *</Label>
                      <Select
                        value={addForm.mare_id || ""}
                        onValueChange={(v) => {
                          const stock = breedingStock.find((h) => h.id === v);
                          setAddForm({
                            ...addForm,
                            mare_id: v,
                            mare_name: stock?.name || "",
                          });
                        }}
                      >
                        <SelectTrigger data-testid="breeding-mare-picker">
                          <SelectValue placeholder={breedingStock.length ? "Pick a mare from the herd" : "No mares/fillies in the horse roster yet"} />
                        </SelectTrigger>
                        <SelectContent>
                          {breedingStock.length === 0 && (
                            <div className="px-2 py-3 text-xs text-muted-foreground">
                              Add a horse with sex &ldquo;mare&rdquo; or &ldquo;filly&rdquo; on the Horses page first.
                            </div>
                          )}
                          {breedingStock.map((h) => (
                            <SelectItem key={h.id} value={h.id} data-testid={`breeding-mare-option-${h.id}`}>
                              {h.name}
                              <span className="text-muted-foreground ml-2">
                                · {h.sex}{h.age != null ? `, ${h.age}yr` : ""}
                                {h.location ? ` · ${h.location}` : ""}
                                {h.injured ? " · injured" : ""}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Every mare on the Breeding page must be a horse in your roster. Edit her name / location / injury status on the Horses page and it&apos;ll reflect here automatically.
                      </p>
                    </div>
                    <div>
                      <Label>Sire</Label>
                      <Input value={addForm.sire_name} list="breeding-sire-suggestions"
                             onChange={(e) => setAddForm({ ...addForm, sire_name: e.target.value })}
                             placeholder="Type name (external OK)"
                             data-testid="breeding-sire-name" />
                      <datalist id="breeding-sire-suggestions">
                        {breedingStock.map((h) => (<option key={`sire-${h.id}`} value={h.name} />))}
                      </datalist>
                    </div>
                    <div>
                      <Label>Dam</Label>
                      <Input value={addForm.dam_name} list="breeding-dam-suggestions"
                             onChange={(e) => setAddForm({ ...addForm, dam_name: e.target.value })}
                             placeholder="Type name (external OK)"
                             data-testid="breeding-dam-name" />
                      <datalist id="breeding-dam-suggestions">
                        {breedingStock.map((h) => (<option key={`dam-${h.id}`} value={h.name} />))}
                      </datalist>
                    </div>
                    <div className="col-span-2">
                      <Label>Sire of dam (maternal grandsire)</Label>
                      <Input value={addForm.sire_of_dam}
                             onChange={(e) => setAddForm({ ...addForm, sire_of_dam: e.target.value })}
                             data-testid="breeding-sire-of-dam" />
                    </div>
                    <div>
                      <Label>Conceived date</Label>
                      <Input type="date" value={addForm.conceived_date}
                             onChange={(e) => setAddForm({ ...addForm, conceived_date: e.target.value })} />
                    </div>
                    <div>
                      <Label>Due date</Label>
                      <Input type="date" value={addForm.due_date}
                             onChange={(e) => setAddForm({ ...addForm, due_date: e.target.value })}
                             data-testid="breeding-due-date" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={addForm.status} onValueChange={(v) => setAddForm({ ...addForm, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_LABELS).map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Notes</Label>
                      <Textarea rows={3} value={addForm.notes}
                                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button onClick={createRecord} disabled={adding} data-testid="breeding-record-submit">
                      {adding ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )
        }
      />

      {/* Mares in foal */}
      <Card className="rounded-xl overflow-hidden">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Heart className="h-4 w-4 text-[hsl(var(--ap-brass))]" />
            Mares in foal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-sm text-muted-foreground text-center py-8">Loading…</div>
          ) : inFoal.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-10" data-testid="breeding-empty">
              No mares currently in foal.{canWrite ? " Click \"Add mare in foal\" above." : ""}
            </div>
          ) : (
            <div className="divide-y" data-testid="breeding-records-list">
              {inFoal.map((rec) => (
                <RecordRow
                  key={rec.id}
                  rec={rec}
                  expanded={expanded.has(rec.id)}
                  onToggle={() => toggleExpand(rec.id)}
                  onAddVetNote={() => setVetNoteFor(rec)}
                  onDelete={() => deleteRecord(rec)}
                  onOpenFoalHistory={() => openFoalHistory(rec)}
                  canWrite={canWrite}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Other / past records */}
      {others.length > 0 && (
        <Card className="rounded-xl overflow-hidden">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Past records ({others.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {others.map((rec) => (
                <RecordRow
                  key={rec.id}
                  rec={rec}
                  expanded={expanded.has(rec.id)}
                  onToggle={() => toggleExpand(rec.id)}
                  onAddVetNote={() => setVetNoteFor(rec)}
                  onDelete={() => deleteRecord(rec)}
                  onOpenFoalHistory={() => openFoalHistory(rec)}
                  canWrite={canWrite}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity log */}
      <Card className="rounded-xl">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-[hsl(var(--ap-info))]" />
            Activity log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? null : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No log entries yet.</p>
          ) : (
            <div className="space-y-4" data-testid="breeding-activity-log">
              {logs.map((l) => (
                <div key={l.id} className="border-l-2 border-[hsl(var(--ap-brass))] pl-4">
                  <div className="text-xs text-muted-foreground">
                    {safeFormatDate(l.date, "PPP", "")}
                    {l.author ? ` · ${l.author}` : ""}
                  </div>
                  <div className="font-medium mt-0.5">{l.title}</div>
                  {l.body && (
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap mt-1">{l.body}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vet note modal */}
      <Dialog open={!!vetNoteFor} onOpenChange={(v) => !v && setVetNoteFor(null)}>
        <DialogContent data-testid="breeding-vet-note-dialog">
          <DialogHeader>
            <DialogTitle>Vet checkup. {vetNoteFor?.mare_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label>Date (optional)</Label>
              <Input type="date" value={vetNoteForm.date}
                     onChange={(e) => setVetNoteForm({ ...vetNoteForm, date: e.target.value })} />
            </div>
            <div>
              <Label>Vet name</Label>
              <Input value={vetNoteForm.vet_name}
                     onChange={(e) => setVetNoteForm({ ...vetNoteForm, vet_name: e.target.value })}
                     placeholder="e.g. Dr Strydom"
                     data-testid="breeding-vet-name" />
            </div>
            <div>
              <Label>Notes *</Label>
              <Textarea rows={5} value={vetNoteForm.note}
                        onChange={(e) => setVetNoteForm({ ...vetNoteForm, note: e.target.value })}
                        placeholder="Ultrasound readings, observations, next-checkup date…"
                        data-testid="breeding-vet-note-text" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVetNoteFor(null)}>Cancel</Button>
            <Button onClick={addVetNote} disabled={savingVetNote} data-testid="breeding-vet-note-save">
              {savingVetNote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Foal history dialog. opened from the "N total" pill on any row */}
      <Dialog open={!!foalDialogFor} onOpenChange={(v) => !v && setFoalDialogFor(null)}>
        <DialogContent className="max-w-lg" data-testid="breeding-foal-history-dialog">
          <DialogHeader>
            <DialogTitle>Foal history. {foalDialogFor?.mare_name}</DialogTitle>
            <p className="text-xs text-muted-foreground pt-1">
              Every foal linked to this mare across every pregnancy. Auto-synced with the Horses page.
            </p>
          </DialogHeader>
          {foalHistoryLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading foals…
            </div>
          ) : foalHistory.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No foals recorded yet for this mare. Foals are auto-created when a breeding record transitions to <em>Foaled</em>.
            </div>
          ) : (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="breeding-foal-history-list">
              {foalHistory.map((f) => (
                <Link
                  key={f.id}
                  to={`/horses/${f.id}`}
                  className="block p-3 rounded-lg border hover:bg-accent/60 active:bg-accent/80 transition-colors"
                  data-testid={`breeding-foal-history-item-${f.id}`}
                >
                  <div className="flex items-baseline justify-between gap-2 flex-wrap">
                    <span className="font-medium">{f.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {f.birth_date || f.foaled_date ? safeFormatDate(f.birth_date || f.foaled_date, "MMM d, yyyy") : "date unknown"}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-2">
                    <span className="capitalize">{f.sex || "unknown sex"}</span>
                    {f.age != null && <><span>·</span><span>{f.age}yr</span></>}
                    {f.sire_name && <><span>·</span><span>by {f.sire_name}</span></>}
                    {f.location && <><span>·</span><span>{f.location}</span></>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RecordRow({ rec, expanded, onToggle, onAddVetNote, onDelete, onOpenFoalHistory, canWrite }) {
  const dueIn = safeDistanceToNow(rec.due_date, { addSuffix: true }, null);
  const mare = rec.mare;                 // live join from horses collection
  const foal = rec.foal_horse;           // linked foal horse (post-foaling)
  const totalFoals = rec.mare_foal_count ?? 0;
  return (
    <div data-testid={`breeding-record-${rec.id}`}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-accent/60 text-left"
        data-testid={`breeding-record-toggle-${rec.id}`}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            {mare ? (
              <Link
                to={`/horses/${mare.id}`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium hover:underline"
                data-testid={`breeding-mare-link-${rec.id}`}
              >
                {mare.name}
              </Link>
            ) : (
              <span className="font-medium">{rec.mare_name}</span>
            )}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONE[rec.status] || ""}`}>
              {STATUS_LABELS[rec.status] || rec.status}
            </span>
            {rec.sire_name && (
              <span className="text-xs text-muted-foreground">× {rec.sire_name}</span>
            )}
            {mare?.injured && (
              <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--destructive))]">
                · injured
              </span>
            )}
          </div>
          {dueIn && (
            <div className="text-xs text-muted-foreground mt-0.5">
              Due {dueIn} · {rec.due_date}
            </div>
          )}
        </div>

        {/* Foals column. this pregnancy's foal + lifetime count pill */}
        <div className="flex items-center gap-2 shrink-0" data-testid={`breeding-foals-cell-${rec.id}`}>
          {foal ? (
            <Link
              to={`/horses/${foal.id}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--ap-forest)/0.1)] text-[hsl(var(--ap-forest))] px-2 py-0.5 text-xs font-medium hover:bg-[hsl(var(--ap-forest)/0.2)] transition-colors"
              title={`Foal: ${foal.name}`}
              data-testid={`breeding-foal-link-${rec.id}`}
            >
              <Activity className="h-3 w-3" strokeWidth={1.5} /> {foal.name}
            </Link>
          ) : rec.status === "in_foal" ? (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
              expected
            </span>
          ) : null}
          {totalFoals > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenFoalHistory?.(); }}
              className="rounded-full bg-[hsl(var(--ap-brass)/0.18)] text-[hsl(var(--ap-saddle))] text-[10px] uppercase tracking-wider px-2 py-0.5 hover:bg-[hsl(var(--ap-brass)/0.3)] transition-colors"
              data-testid={`breeding-foal-total-${rec.id}`}
              title={`${totalFoals} total foal${totalFoals === 1 ? "" : "s"} by this mare. click to view`}
            >
              {totalFoals} total
            </button>
          )}
        </div>

        {(rec.vet_notes || []).length > 0 && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
            {(rec.vet_notes || []).length} vet note{(rec.vet_notes || []).length === 1 ? "" : "s"}
          </span>
        )}
      </button>
      {expanded && (
        <div className="px-12 pb-4 space-y-3 bg-accent/30" data-testid={`breeding-record-detail-${rec.id}`}>
          <div className="grid grid-cols-2 gap-3 text-sm pt-3">
            <DetailField label="Sire" value={rec.sire_name} />
            <DetailField label="Dam" value={rec.dam_name} />
            <DetailField label="Sire of dam (maternal grandsire)" value={rec.sire_of_dam} />
            <DetailField label="Conceived" value={rec.conceived_date} />
            <DetailField label="Due" value={rec.due_date} />
            <DetailField label="Status" value={STATUS_LABELS[rec.status] || rec.status} />
            {rec.foal?.name && <DetailField label="Foal" value={`${rec.foal.name}${rec.foal.sex ? ` (${rec.foal.sex})` : ""}`} />}
            {rec.foal?.birth_date && <DetailField label="Foal birth" value={rec.foal.birth_date} />}
            {rec.notes && <div className="col-span-2"><DetailField label="Notes" value={rec.notes} /></div>}
          </div>

          {/* Vet notes timeline */}
          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground flex items-center gap-1.5">
                <Stethoscope className="h-3 w-3" /> Vet checkup comments
              </div>
              {canWrite && (
                <Button variant="ghost" size="sm" onClick={onAddVetNote}
                        data-testid={`breeding-add-vet-note-${rec.id}`}>
                  <Plus className="h-3 w-3 mr-1" /> Add note
                </Button>
              )}
            </div>
            {(rec.vet_notes || []).length === 0 ? (
              <div className="text-xs text-muted-foreground py-1">No vet notes yet.</div>
            ) : (
              <div className="space-y-2">
                {(rec.vet_notes || []).slice().reverse().map((n) => (
                  <div key={n.id || n.date} className="border-l-2 border-[hsl(var(--ap-info))] pl-3 py-1">
                    <div className="text-[11px] text-muted-foreground">
                      {safeFormatDate(n.date, "PPP", "")}
                      {n.vet_name ? ` · ${n.vet_name}` : ""}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {canWrite && (
            <div className="flex justify-end pt-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`breeding-delete-${rec.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {rec.mare_name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Deletes the breeding record and all vet notes for this mare.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={onDelete}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >Remove</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="text-sm">{value || ""}</div>
    </div>
  );
}
